import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import config from 'scoreboard/components/config'
import template from './vi-mapping.hbs'
import i18n from 'scoreboard/components/i18n'
import FormView from 'scoreboard/components/form-view'
import ScrollingDialogTable from 'scoreboard/components/scrolling-dialog-table'
import LazyFilteringSelect from 'scoreboard/components/lazy-filtering-select'
import escapeStringRegexp from 'escape-string-regexp'
import selects from 'scoreboard/components/selects'
import Pagination from 'scoreboard/components/pagination'
import rowTemplate from './vi-mapping-table-row.hbs'
import headerRowTemplate from './vi-mapping-table-header-row.hbs'

const resultsPerPage = 1000

const ViMapping = Backbone.View.extend({

	tagName: 'form',
	className: 'vi-wizard',
	events: {
		'change .import-metric': 'renderSelects'
	},

	initialize: function (options) {
		this.options = options || {}
		this.destinationItems = this.options.model.get('destinationItems') || []

		const MyFormView = FormView.extend({
			test: this.test.bind(this)
		})
		this.formView = new MyFormView({
			el: this.el,
			guard: false,
			submit: this.submit.bind(this),
			allowCleanSubmission: () => _.isNil(this.test())
		})
	},

	render: function () {
		let context = this.context()
		this.$el.empty().append(template(context))
		this.options.wizard.render({
			formView: this.formView,
			stateSaver: this.saveState.bind(this)
		})

		let needsPagination = this.destinationItems.length > resultsPerPage
		let createScrollingDialogTable = () => {
			// Set the table's size so that it fits comfortably within the dialog
			// and scrolls if need be.
			this.scrollingDialogTable = new ScrollingDialogTable({
				el: this.$('.panel-content'),
				siblings: [this.$('.banner')],
				staticElementsHeight: needsPagination ? 370 : 300
			}).render()
		}

		if (needsPagination) {
			this.pagination = new Pagination({
				data: this.destinationItems,
				headerRowTemplate,
				// There can be a very large amount of rows and select options here in this pagination control.
				// Building rows manually is faster than passing the pagination control a template.
				row: d => {
					let tr = document.createElement('tr')
					let id = document.createElement('td')
					id.innerHTML = d.id

					let orgLabel = document.createElement('td')
					orgLabel.innerHTML = _.escape(d.organization.label)

					let label = document.createElement('td')
					label.innerHTML = _.escape(d.label)

					let selectCell = document.createElement('td')
					let selectWrap = document.createElement('div')
					selectWrap.className = 'select-wrap lazy-select'
					let select = document.createElement('select')
					select.className = 'import-metric'
					select.setAttribute('name', `metric-${d.id}`)
					select.setAttribute('data-id', d.id)
					selectWrap.appendChild(select)
					selectCell.appendChild(selectWrap)

					tr.append(...[id, orgLabel, label, selectCell])

					return tr
				},
				pageSize: resultsPerPage,
				setupTableBody: () => {
					// We're populating a new set of rows with new selects so nix lazyOptions
					// so that new lazy selects get setup in renderSelects()
					delete this.lazyOptions
					this.renderSelects()

					// Now that we've just gone through the hefty process of rendering the pagination table
					// and then rendering the selects for the current page, call this.formView.setDirty()
					// to force the already-loaded formView to retest things now that we're all done
					// building it's contents. #36344
					this.formView.setDirty()
				}
			})

			this.$('.panel-with-table').replaceWith(this.pagination.el)
			this.pagination.render()
		} else {
			let head, bodyRows
			head = `<thead>${headerRowTemplate(context)}</thead>`
			_.each(context.destinationItems, di => bodyRows += rowTemplate(di))
			this.$('.table').append(head + `<tbody>${bodyRows}</tbody>`)
			this.renderSelects()
		}

		createScrollingDialogTable()
		this.formView.start()

		return this
	},

	/**
	 * Prepares this.selectedDestinations by initializing it with destinationMappingData from
	 * this.model or updating it according to what's been selected/deselected in the table
	 *
	 * @param  {Event} [e] the possible event coming from a select change event which is
	 * to be used to update this.selectedDestinations
	 */
	_configureSelectedDestinations(e) {
		let hasNoDestinations = _.isNil(e)
		// If an event did NOT trigger this method (i.e. render() method)), we need
		// to get the options based on the mapping data.
		if (hasNoDestinations) {
			// Update with any automapping/previously mapped destination metrics
			/**
			 * @type {Object} where keys are option values (labels) and the values are the select ids
			 */
			this.selectedDestinations = this.model.get('destinationMappingData').sourceNameToDestinationIdMap
		} else {
			// If the method WAS triggered by an event, we will configure the new options
			// based on the event selection.
			let target = _.get(e, 'target')
			let chosen = _.get(e, 'target.value', '')
			let targetId = parseInt(target.dataset.id)
			if (hasNoDestinations) {
				this.selectedDestinations = {}
			}

			_.each(this.selectedDestinations, (id, val) => {
				if (targetId == id) {
					delete this.selectedDestinations[val]
				}
			})

			if (chosen != '') {
				this.selectedDestinations[chosen] = targetId
			}
		}
	},

	/**
	 * Rounds up and categorizes all options to be fed into a lazy select.
	 * @return {Object}   A map of select options
	 */
	_getAllSourceOptions() {
		const unusedLabel = i18n.get('admin.import.unused')
		const usedLabel = i18n.get('admin.licenseUsage.used')
		let sourceNames = this.model.get('destinationMappingData').sourceNames
		let allSourceOptions = _.map(sourceNames, source => {
			return {
				label: source,
				value: source,
				group: this.selectedDestinations[source] ? usedLabel : unusedLabel,
				// 'type' is the default groupSortAttribute
				type: this.selectedDestinations[source] ? 'used' : 'unused'
			}
		})
		// add an empty option to the top to make it easier to clear a selection
		allSourceOptions.unshift({label: '', value: '', group: 'headerless'})
		return allSourceOptions
	},

	/**
	 * Returns a copy of the grouped options with the appropriate option
	 * set to `selected: true` for the given select
	 *
	 * @param {Function} [options.findPredicate] find a single option
	 * (selected value; lazy-filtering-select)
	 */
	_getOptionsWithSelected(select, options = {}) {
		const selectId = $(select).data('id')
		const selectedDestination = _.findKey(this.selectedDestinations, id => id == selectId)

		let sourceOptions = _.map(this._getAllSourceOptions(), o => {
			o.selected = o.value == selectedDestination
			return o
		})

		// If we're just looking for the selected item we should short-circuit
		// as soon as we find it and avoid any further processing (e.g. sorting)
		if (options.findPredicate) {
			const found = _.find(sourceOptions, options.findPredicate)
			return found ? [found] : []
		}

		return selects.sortOptions({
			groupBy: 'group',
			groupSortOrder: ['unused', 'used']
		}, sourceOptions)
	},

	renderSelects: function(e) {
		// Setting select values will trigger change events, which will call
		// renderSelects and get us stuck in an infinite loop. So we'll use a
		// flag to prevent that
		if (this.renderingSelects
			// Also, if this has been triggered from a target which isn't a select,
			// ignore it. https://github.com/SpiderStrategies/Scoreboard/issues/33941
			|| (!_.isNil(e) && e.target.nodeName != 'SELECT')) {
			return
		}
		this.renderingSelects = true

		this._configureSelectedDestinations(e)

		const groupingRegex = new RegExp(config.groupingSeparator, 'g')
		const removeGroupingSeparator = t => _.replace(t, groupingRegex, '')

		var createLazyFilteringSelect = select => {
			var options = {
				el: select.closest('.lazy-select'),
				maxFilteringResults: 1000, // Limit results. See #33878
				// Each time a select is clicked on, pull the available/sorted
				// select options from _getOptionsWithSelected()
				getSelectOptions: opts => this._getOptionsWithSelected(select, opts),
				filteringOptions: {
					render: {
						option: (d, term) => selects.optionHtml(d, term, {
							termRegexFn: searchTerm => {
								const regex = escapeStringRegexp(_.escape(searchTerm))
								let searchRegex = regex
								if (d.searchMatchRegexDigitSuffix) {
									searchRegex = ''
									for (const c of regex) {
										searchRegex += c
										if (!_.isNaN(parseInt(c))) {
											// Optionally ignore character(s) in the match text (see vi-mapping.js)
											// example: searchTerm of '25'
											// regex would be: `2[,]?5` and will highlight [2,5]00 or 3,4[25]
											searchRegex += `[${d.searchMatchRegexDigitSuffix}]?`
										}
									}
								}
								// escape potential HTML in the searchTerm so special characters are highlighted properly
								return new RegExp(searchRegex, 'i')
							}
						})
					},
					groupBy: 'group',
					sort: [], // disables sort, we do this ourselves above
					fieldAdapters: {
						// #32712
						'label': (l, data = {}) => {
							data.searchMatchRegexDigitSuffix = config.groupingSeparator
							return removeGroupingSeparator(l)
						}
					},
					// Limit the number of native select options rendered to the DOM
					// for better pagination performance.
					nativeSupport: false
				}
			}

			return new LazyFilteringSelect(options).render()
		}

		// This screen can have hundreds or thousands of mappings, each with its
		// own filter select. That performs poorly, so we'll use "lazy" selects
		// that won't turn into rich filtering selects until they're clicked
		if (this.lazyOptions) {
			// If we've just changed the select to the empty option, there's nothing to do here. If we continue, we
			// might end up rebuilding a bunch of selects that also have blank values #36265.
			if (e.target.value == '') {
				this.renderingSelects = false
				return
			}

			// If we've already rendered the lazy selects, go through them to make
			// sure all mappings are unique and that there are no rich selects
			// lingering which will end up clogging up the DOM, making things slower.
			let selectedId = e.target.dataset.id
			this.lazyOptions.forEach(lazy => {
				let lazySelect = lazy.$('select')[0]
				let id = lazySelect.dataset.id

				// Skip the select we just changed
				if (id == selectedId) {
					lazy.options.getSelectOptions = () => this._getOptionsWithSelected(lazySelect)
					return
				}

				// If this lazy select has the same value as the select we just changed,
				// blow it away and recreate it. Also, if there are any selects which
				// still have their many options rendered, recreate them as well with just
				// the one option they need to keep the DOM free from clutter.
				if (e.target.value == lazySelect.value || lazySelect.classList.contains('has-grouped-menu')) {
					_.remove(this.lazyOptions, lo => lo.el.dataset.id == id)

					lazy.$el.empty().addClass('select-wrap')
					lazy.$el.append(`<select name="metric-${id}" data-id="${id}" class="import-metric"></select>`)

					this.lazyOptions.push(createLazyFilteringSelect(lazy.$('select')))
				}
			})
		} else {
			this.lazyOptions = []
			_.each(this.$('select.import-metric'), select => {
				// Because the set of options in every select will have one option that
				// changes groups, we need to include this in the loop rather.
				this.lazyOptions.push(createLazyFilteringSelect(select))
			})
		}

		this.renderingSelects = false
	},

	context: function () {
		let type = this.model.get('type')
		let instructionKey, itemKey
		if (type === 'initiative-status') {
			instructionKey = 'admin.import.mappings.instructions.initiative'
			itemKey = 'initiatives.initiativeItem'
		} else {
			instructionKey = 'admin.import.mappings.instructions.metric'
			itemKey = 'scorecards.scorecardNode'
		}
		return {
			title: i18n.get('admin.import.mapping'),
			destinationItems: this.destinationItems,
			instructions: i18n.get(instructionKey),
			itemHeader: i18n.get(itemKey)
		}
	},

	test: function () {
		const problem = FormView.prototype.test.apply(this, arguments)
		if (problem) {
			return problem
		}
		// at least one metric should be mapped
		if (_.isNil(this.selectedDestinations) || Object.keys(this.selectedDestinations).length == 0) {
			return i18n.get('admin.import.mapping.pleaseMap')
		}
	},

	saveState: function () {
		if (!this.model.get('destinationMappingData')) {
			this.model.set('destinationMappingData', {})
		}
		this.model.get('destinationMappingData').sourceNameToDestinationIdMap =
			this.selectedDestinations
	},

	submit: function () {
		this.saveState()
		let lastStep = this.options.wizard.step == this.options.wizard.steps
		if (lastStep) {
			// #28185 clear import status when editing mappings
			let url = _.result(this.model, 'url')
			url += '/clear'
			this.model.save({}, {
				url,
				success: () => {
					this.trigger('done', this.model)
					this.options.wizard.finish()
				},
				error: (model, response) => this.formView.error(response)
			})
		} else {
			this.options.wizard.next()
		}
	},

	remove: function () {
		delete this.lazyOptions
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.scrollingDialogTable, this.pagination], 'remove')
	}

})

export default ViMapping

ViMapping.__filename__ = 'imports/vi/vi-mapping.js'
